import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { BusinessFields } from '../components/BusinessFields.js';
import { CreditOrderBuilder } from '../components/CreditOrderBuilder.js';
import { useLiveOffer } from '../components/OfferLine.js';
import { SubmitRefusalLine, useSubmitRefusal } from '../components/SubmitRefusal.js';
import { RefusalNotice, useWritesRefused } from '../components/SuspendedNotice.js';
import { Alert, Button, Card } from '../components/ui.js';
import { ApiError } from '../lib/api.js';
import {
  fetchCreditPacks,
  fetchLedger,
  fetchPayments,
  startCheckout,
  type CreditOrderRequest,
  type CreditPacksResponse,
  type LedgerResponse,
  type PaymentRecord,
} from '../lib/creditsApi.js';
import {
  describeLedgerEntry,
  formatCredits,
  formatDateTime,
  formatMoney,
  formatSignedCredits,
} from '../lib/format.js';
import { updateAgencySettings } from '../lib/agencyApi.js';
import { websiteError as localWebsiteError } from '../lib/businessWebsite.js';
import { BUY_BLOCK_MESSAGES, buyBlockReason } from '../lib/buyGate.js';
import { redirectTo } from '../lib/navigation.js';
import { forgetOffer, rememberedOffer } from '../lib/offer.js';
import { useSession } from '../session/SessionProvider.js';

/** How long to keep checking for credits after returning from the payment page. */
const CREDIT_POLL_ATTEMPTS = 6;
const CREDIT_POLL_INTERVAL_MS = 2000;

/** One observation of the agency's credit position: the two calls, together. */
interface LoadedCredits {
  ledger: LedgerResponse;
  payments: PaymentRecord[];
}

/**
 * The renewal deep link's parameter — **renamed by D-9 ruling 9**.
 *
 * It was `?credits=N`. The re-denomination made every credit worth ten times
 * what it was, so a link sitting in a renewal reminder sent last month would
 * have opened this page prefilled with **ten times** the money the agency
 * needed — `?credits=600` meaning $600 on Tuesday and $6,000 on Wednesday.
 *
 * A rename is what makes that impossible rather than merely unlikely. The old
 * parameter is not read, not redirected and not translated: an old link opens
 * an empty builder, which is a mild inconvenience, where the alternative is a
 * $6,000 order sitting prefilled in front of someone in a hurry.
 *
 * One constant, referenced by the page and by everything that builds the link.
 */
export const BUY_PARAM = 'buy';

export function CreditsPage(): React.JSX.Element {
  const { session, refresh } = useSession();
  const agencyId = session?.agency?.id ?? null;
  // Suspended, or an operator viewing read-only - WAIT-B defect 3.
  const { refused: suspended } = useWritesRefused();
  /*
   * **D-16 item 6.** The page's copy of the gate, so nobody prices an order the
   * server will refuse. `POST /api/checkout/sessions` asks its own copy on
   * every request, so a stale bundle or a bookmark still meets a 403.
   */
  const buyBlock = buyBlockReason(session?.agency ?? null);
  const [searchParams, setSearchParams] = useSearchParams();
  const purchase = searchParams.get('purchase');

  const [catalogue, setCatalogue] = useState<CreditPacksResponse | null>(null);
  const [ledger, setLedger] = useState<LedgerResponse | null>(null);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [buying, setBuying] = useState(false);
  const [couponRefusal, setCouponRefusal] = useState<string | null>(null);

  /*
   * C-133. The offer the sign-up address named, kept since then by
   * `useRememberOfferFromAddress`. Read once, on mount, and shown beside the
   * code box if the server still says it is live. **Not applied**: the buyer
   * types it, so once per account and every other rule work as they always
   * did. Forgotten once it has been on screen, which makes this the first
   * visit's line and nobody's banner for ever after.
   */
  const [offerCode] = useState(rememberedOffer);
  const liveOffer = useLiveOffer(offerCode);

  /**
   * The renewals deep link: `/credits?buy=N` opens the builder with the
   * shortfall already entered.
   *
   * Read once, on mount. Re-reading would fight the buyer for the field every
   * time the URL changed underneath them.
   */
  const prefilledCredits = useRef(readBuyParam(searchParams.get(BUY_PARAM)));

  /** Balance when we arrived back from the payment page, to detect the credit landing. */
  const balanceOnReturn = useRef<number | null>(null);
  const [awaitingCredits, setAwaitingCredits] = useState(purchase === 'success');
  const [creditsArrived, setCreditsArrived] = useState(false);
  /**
   * Whether the first load is also the return from a payment. A ref, so that
   * dismissing the banner (which drops `?purchase=` from the URL) does not
   * count as a change worth reloading everything for.
   */
  const returningFromPayment = useRef(purchase === 'success');

  const load = useCallback(async (): Promise<LoadedCredits | null> => {
    if (!agencyId) return null;
    const [ledgerResponse, paymentsResponse] = await Promise.all([
      fetchLedger(agencyId),
      fetchPayments(),
    ]);
    setLedger(ledgerResponse);
    setPayments(paymentsResponse.payments);
    return { ledger: ledgerResponse, payments: paymentsResponse.payments };
  }, [agencyId]);

  /**
   * Did this load show the purchase as credited?
   *
   * Two independent signals, because either one can be the first to be seen:
   *
   *   - the balance is higher than it was when we got back from the payment
   *     page, which is what polling sees when the webhook lands while this
   *     page is open;
   *   - the newest payment is already in `credited` state, which is what the
   *     very first load sees when the webhook beat the browser back. The
   *     balance comparison is blind to that case — the baseline it captured
   *     already includes the credit, so nothing will ever be "higher" — and
   *     that is the bug this closes: the balance on screen was right while the
   *     banner still said the credits were taking longer than usual.
   */
  const observedCredited = useCallback((loaded: LoadedCredits | null): boolean => {
    if (!loaded) return false;

    const started = balanceOnReturn.current;
    if (started !== null && loaded.ledger.credits.ledgerBalance > started) return true;

    // The API returns payments newest first, and checkout creates the row
    // before the browser ever leaves, so the newest one is this purchase.
    return loaded.payments[0]?.status === 'credited';
  }, []);

  const settle = useCallback(
    async (loaded: LoadedCredits | null): Promise<boolean> => {
      if (!observedCredited(loaded)) return false;
      setCreditsArrived(true);
      setAwaitingCredits(false);
      await refresh();
      return true;
    },
    [observedCredited, refresh],
  );

  useEffect(() => {
    if (!agencyId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const [packsResponse, loaded] = await Promise.all([fetchCreditPacks(), load()]);
        if (cancelled) return;
        setCatalogue(packsResponse);
        // The first load is also an observation: if the webhook got here
        // first, this is where we find out, and the banner must resolve.
        if (returningFromPayment.current) await settle(loaded);
        if (balanceOnReturn.current === null && loaded) {
          balanceOnReturn.current = loaded.ledger.credits.ledgerBalance;
        }
        setError(null);
      } catch {
        if (!cancelled) setError('We could not load your credits.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [agencyId, load, settle]);

  /**
   * Payment providers confirm out of band, so the credits may not have landed
   * by the time the browser comes back. Rather than showing a stale zero, keep
   * looking for a short while and say plainly what is happening.
   */
  useEffect(() => {
    if (!awaitingCredits || !agencyId) return;

    let cancelled = false;
    let attempts = 0;

    const timer = setInterval(() => {
      attempts += 1;
      void (async () => {
        try {
          const loaded = await load();
          if (cancelled) return;
          if (await settle(loaded)) return;
        } catch {
          // Keep waiting; the next tick tries again.
        }
        if (attempts >= CREDIT_POLL_ATTEMPTS && !cancelled) setAwaitingCredits(false);
      })();
    }, CREDIT_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [awaitingCredits, agencyId, load, settle]);

  async function handleBuy(order: CreditOrderRequest): Promise<void> {
    setError(null);
    setCouponRefusal(null);
    setBuying(true);
    try {
      // Quantities, not a price. The server prices this again from the same
      // engine that produced the quote on screen.
      const { redirectUrl } = await startCheckout(order);
      redirectTo(redirectUrl);
    } catch (caught) {
      setBuying(false);
      /*
       * C-106: an earlier checkout with this once-per-account code was paid a
       * moment ago. The server's own sentence, by the code box it is about.
       */
      if (
        caught instanceof ApiError &&
        caught.status === 409 &&
        (caught.details as { reason?: unknown } | undefined)?.reason === 'coupon_already_used'
      ) {
        setCouponRefusal(caught.message);
        return;
      }
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'We could not start your payment. Please try again.',
      );
    }
  }

  function dismissPurchaseBanner(): void {
    const next = new URLSearchParams(searchParams);
    next.delete('purchase');
    setSearchParams(next, { replace: true });
    setAwaitingCredits(false);
    setCreditsArrived(false);
  }

  if (!agencyId) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-semibold tracking-tight">Credits</h1>
        <Alert tone="info">Operator accounts do not hold credits.</Alert>
      </div>
    );
  }

  const credits = ledger?.credits ?? session?.credits ?? null;

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Credits</h1>
        <p className="mt-1 text-sm text-fg-muted">
          Buy credits, then spend them on site builds for your clients.
        </p>
      </div>

      {purchase === 'success' ? (
        <PurchaseBanner
          awaiting={awaitingCredits}
          arrived={creditsArrived}
          onDismiss={dismissPurchaseBanner}
        />
      ) : null}

      {purchase === 'cancelled' ? (
        <Alert tone="info">
          Your payment was cancelled and you have not been charged.{' '}
          <button type="button" onClick={dismissPurchaseBanner} className="underline">
            Dismiss
          </button>
        </Alert>
      ) : null}

      {error ? <Alert>{error}</Alert> : null}

      <div className="grid gap-4 sm:grid-cols-3">
        <Stat label="Available to spend" value={formatCredits(credits?.availableCredits ?? 0)} />
        <Stat label="Held for open orders" value={formatCredits(credits?.reservedCredits ?? 0)} />
        <Stat label="Total balance" value={formatCredits(credits?.ledgerBalance ?? 0)} />
      </div>

      {/* Buy credits leads, by operator ruling at WAIT-CO3. This reverses
          ruling D-5, which put history first on the reasoning that someone
          opening this page more often asks "where did my credits go?" than
          "what can I buy?". With the order builder here rather than four fixed
          cards, buying is what the page is for.

          Ordering only. The larger presentation change is ruled and follows in
          a later change order; the cards are deliberately not restyled. */}
      <section>
        <h2 className="text-lg font-semibold tracking-tight text-fg">Buy credits</h2>
        <p className="mt-1 text-sm text-fg-muted">
          Payment is taken on our provider&rsquo;s secure page. Your card details never reach us.
        </p>

        {/*
          P-4. The suspension is checked before the catalogue is even looked
          at: a builder that prices an order the server will refuse is a form
          somebody fills in to be told no at the end of it.
        */}
        {suspended ? (
          <div className="mt-4">
            <RefusalNotice action="buy credits" />
          </div>
        ) : buyBlock ? (
          /*
           * **D-16 item 6, and the ordering is deliberate.** Suspension is
           * checked first: an account that is both suspended and missing its
           * business details should be told the thing that is actually
           * stopping it, and a suspended account cannot buy whatever its tax
           * position is.
           *
           * Under view-as this branch is unreachable, because `useWritesRefused`
           * has already returned true above and shown the read-only line. That
           * is the "viewing wins" rule, and it is right here too: an operator
           * looking at an account does not need to be told to add their
           * business website.
           */
          <div className="mt-4">
            <Alert tone="warning">
              <p>{BUY_BLOCK_MESSAGES[buyBlock]}</p>
              {/*
                F-2, WAIT-BMC: the link to Settings is gone for this reason.
                The form that finishes the job is directly below, and Settings
                has no business tickbox, so the link sent people to a page
                where they could not finish. The other two reasons are about
                the VAT number, which lives in Settings, and keep it.
              */}
              {buyBlock === 'business_details' ? null : (
                <p className="mt-2">
                  <Link to="/settings" className="font-medium underline">
                    Go to Account settings
                  </Link>
                </p>
              )}
            </Alert>
            {/*
              **Ruling 12's one-time prompt.** The three accounts that predate
              0020 gave neither of these, because neither was ever asked for.
              Sending them to Settings works, but this is the moment they
              actually want to buy, and a form here means they can finish and
              carry on rather than navigate away and come back.

              Only for `business_details`: a missing VAT number is a thing to go
              and find, not a thing to type from memory, so that one keeps the
              link alone.
            */}
            {buyBlock === 'business_details' && agencyId ? (
              <BusinessDetailsPrompt
                agencyId={agencyId}
                savedWebsite={session?.agency?.businessWebsite ?? null}
                onDone={refresh}
              />
            ) : null}
          </div>
        ) : loading ? (
          <p className="mt-4 text-sm text-fg-muted" role="status">
            Loading…
          </p>
        ) : catalogue && catalogue.packs.length > 0 ? (
          <CreditOrderBuilder
            catalogue={catalogue}
            initialCustomCredits={prefilledCredits.current}
            offer={liveOffer}
            onOfferShown={forgetOffer}
            busy={buying}
            couponRefusal={couponRefusal}
            onCouponChange={() => {
              setCouponRefusal(null);
            }}
            onCheckout={(order) => void handleBuy(order)}
          />
        ) : (
          <Alert tone="info">Buying credits is not available at the moment.</Alert>
        )}

        {catalogue && catalogue.disclosure.length > 0 ? (
          <div className="mt-5 rounded-box bg-sunken px-4 py-3">
            <h3 className="text-sm font-medium text-fg">Before you buy</h3>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-fg-2">
              {catalogue.disclosure.map((line) => (
                <li key={line}>{line}</li>
              ))}
              {/* D-8: the one thing the old terms did not have to explain. */}
              <li>Quantities combine into a single order, priced on the total.</li>
            </ul>
          </div>
        ) : null}
      </section>
      <section>
        <h2 className="text-lg font-semibold tracking-tight text-fg">Credit history</h2>
        <Card className="mt-4 p-0">
          {loading ? (
            <p className="p-6 text-sm text-fg-muted" role="status">
              Loading…
            </p>
          ) : ledger && ledger.entries.length > 0 ? (
            // At 360px the date, the reason and the figure are wider than the
            // card, so the history scrolls inside it rather than the page.
            <div className="overflow-x-auto" data-testid="ledger-scroll">
              <table className="w-full text-left text-sm">
                <thead className="border-b border-rule text-fg-muted">
                  <tr>
                    <th scope="col" className="px-6 py-3 font-medium">
                      Date
                    </th>
                    <th scope="col" className="px-6 py-3 font-medium">
                      What happened
                    </th>
                    <th scope="col" className="px-6 py-3 text-right font-medium">
                      Credits
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-rule-soft">
                  {ledger.entries.map((entry) => (
                    <tr key={entry.id}>
                      <td className="px-6 py-3 whitespace-nowrap text-fg-muted">
                        {formatDateTime(entry.createdAt)}
                      </td>
                      <td className="px-6 py-3 text-fg">
                        {describeLedgerEntry(entry.entryType)}
                        {entry.reason ? (
                          <span className="block text-fg-muted">{entry.reason}</span>
                        ) : null}
                      </td>
                      <td
                        className={`px-6 py-3 text-right font-medium font-mono tabular-nums ${
                          entry.amount > 0 ? 'text-ok' : 'text-fg'
                        }`}
                      >
                        {formatSignedCredits(entry.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-6">
              <p className="text-sm font-medium text-fg">No credit history yet</p>
              <p className="mt-1 text-sm text-fg-muted">
                Once you buy your first credits they will appear here.
              </p>
            </div>
          )}
        </Card>
      </section>

      <section>
        <h2 className="text-lg font-semibold tracking-tight text-fg">Receipts</h2>
        <Card className="mt-4 p-0">
          {loading ? (
            <p className="p-6 text-sm text-fg-muted" role="status">
              Loading…
            </p>
          ) : payments.length > 0 ? (
            <ul className="divide-y divide-rule-soft">
              {payments.map((payment) => (
                <li key={payment.id} className="flex items-center gap-4 px-6 py-4 text-sm">
                  <div className="min-w-0 flex-1">
                    {/*
                      The headline is what the card was charged - D-13, item 1c.

                      It used to be `amountMinor`, which is now the goods price
                      with the tax still to come. Leading with that would
                      understate every taxed purchase on a screen whose heading
                      is "Receipts".
                    */}
                    <p className="font-medium text-fg">
                      {formatMoney(payment.totalMinor, payment.currency)} ·{' '}
                      {formatCredits(payment.creditsPurchased)}
                    </p>
                    <p className="text-fg-muted">
                      {formatDateTime(payment.createdAt)} · {describePaymentStatus(payment)}
                    </p>
                    {/*
                      The split, only where there is one. "$50.00 plus $0.00
                      tax" on an untaxed purchase is a line that makes a reader
                      look for something that is not there.
                    */}
                    {payment.taxMinor > 0 ? (
                      <p className="text-fg-muted" data-testid={`tax-breakdown-${payment.id}`}>
                        {formatMoney(payment.amountMinor, payment.currency)} plus{' '}
                        {formatMoney(payment.taxMinor, payment.currency)} tax
                      </p>
                    ) : null}
                  </div>
                  {payment.receiptUrl ? (
                    <a
                      href={payment.receiptUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="shrink-0 font-medium text-fg underline"
                    >
                      View receipt
                    </a>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-6">
              <p className="text-sm font-medium text-fg">No purchases yet</p>
              <p className="mt-1 text-sm text-fg-muted">
                Receipts for the credits you buy will appear here.
              </p>
            </div>
          )}
        </Card>
      </section>

      {/* The packs stay on this page, below the history (ruling D-5). */}
    </div>
  );
}

/**
 * The `?buy=` deep link value, if it is one.
 *
 * Undefined rather than a guess when the parameter is absent or nonsense — the
 * builder then simply opens empty. The server clamps the figure again when it
 * prices, so this is about not prefilling rubbish, not about safety.
 */
function readBuyParam(raw: string | null): number | undefined {
  if (raw === null) return undefined;
  const value = Number(raw);
  if (!Number.isFinite(value) || value <= 0) return undefined;
  return Math.ceil(value);
}

function Stat({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <Card>
      <p className="text-sm font-medium text-fg-muted">{label}</p>
      <p className="mt-2 font-display text-3xl font-semibold tracking-tight text-fg">{value}</p>
    </Card>
  );
}

function PurchaseBanner({
  awaiting,
  arrived,
  onDismiss,
}: {
  awaiting: boolean;
  arrived: boolean;
  onDismiss: () => void;
}): React.JSX.Element {
  if (awaiting) {
    return (
      <Alert tone="info">
        Payment received. Adding your credits. This usually takes a few seconds.
      </Alert>
    );
  }

  if (arrived) {
    return (
      <Alert tone="success">
        Your credits have been added.{' '}
        <button type="button" onClick={onDismiss} className="underline">
          Dismiss
        </button>
      </Alert>
    );
  }

  return (
    <Alert tone="info">
      Payment received. Your credits are taking longer than usual to appear. Refresh in a minute,
      and get in touch if they are still missing.{' '}
      <button type="button" onClick={onDismiss} className="underline">
        Dismiss
      </button>
    </Alert>
  );
}

function describePaymentStatus(payment: PaymentRecord): string {
  switch (payment.status) {
    case 'credited':
      return 'Credits added';
    case 'settled':
      return 'Paid · adding credits';
    case 'initiated':
      return 'Awaiting payment';
    case 'failed':
      return payment.failureReason ?? 'Payment did not complete';
    default:
      return payment.status;
  }
}


/**
 * The one-time prompt for an account that predates D-16 - ruling 12.
 *
 * **Not a second sign-up.** It asks for exactly the two things the gate is
 * missing, saves them through the ordinary settings route, and gets out of the
 * way: the session refreshes, `buyBlockReason` goes null, and the builder
 * appears where this was.
 */
/** F-3: what the Credits prompt says under each field when it is pressed incomplete. */
export const BUSINESS_PROMPT_REFUSALS = {
  website: 'Add your business website',
  confirmed: "Tick to confirm you're buying for your business",
} as const;

function BusinessDetailsPrompt({
  agencyId,
  savedWebsite,
  onDone,
}: {
  agencyId: string;
  /**
   * F-2: a website already saved in Settings is shown here, not asked for
   * again. The confirmation still has to be ticked: Settings has no tickbox,
   * so a saved website is half the job.
   */
  savedWebsite: string | null;
  onDone: () => Promise<void>;
}): React.JSX.Element {
  const [website, setWebsite] = useState(savedWebsite ?? '');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [websiteRefusal, setWebsiteRefusal] = useState<string | undefined>(undefined);
  const [confirmRefusal, setConfirmRefusal] = useState<string | undefined>(undefined);
  const refusal = useSubmitRefusal();

  /*
   * **Always enabled** - F-3, 30 September 2026. A grey button beside a grey
   * box read as a form that was switched off. Pressing it with something
   * missing now says what, under the field it is about, puts the red line by
   * the button and moves to the first problem, as the four other forms do.
   * The server's own check is unchanged and still has the last word.
   */
  async function save(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    refusal.clear();
    setError(null);

    const typed = website.trim();
    const missingWebsite = typed === '' ? BUSINESS_PROMPT_REFUSALS.website : undefined;
    // A typed but malformed or placeholder address already shows its own
    // message from `BusinessFields`; it still refuses the save.
    const badWebsite = missingWebsite ?? (typed === '' ? undefined : localWebsiteError(typed));
    const missingTick = confirmed ? undefined : BUSINESS_PROMPT_REFUSALS.confirmed;

    if (badWebsite !== undefined || missingTick !== undefined) {
      setWebsiteRefusal(missingWebsite);
      setConfirmRefusal(missingTick);
      refusal.refuseFields();
      return;
    }

    setBusy(true);
    try {
      await updateAgencySettings(agencyId, {
        businessWebsite: typed,
        businessConfirmed: true,
      });
      await onDone();
    } catch (caught) {
      const message = caught instanceof ApiError ? caught.message : 'We could not save that.';
      setError(message);
      refusal.refuse(message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      ref={refusal.formRef}
      noValidate
      onSubmit={(event) => void save(event)}
      className="mt-4 space-y-4 rounded-box bg-subtle p-4"
    >
      {error ? <Alert>{error}</Alert> : null}
      <BusinessFields
        website={website}
        onWebsiteChange={(value) => {
          setWebsite(value);
          setWebsiteRefusal(undefined);
        }}
        websiteError={websiteRefusal}
        confirmed={confirmed}
        onConfirmedChange={(value) => {
          setConfirmed(value);
          setConfirmRefusal(undefined);
        }}
        confirmedError={confirmRefusal}
      />
      <SubmitRefusalLine message={refusal.message} />
      <Button type="submit" busy={busy}>
        Save and continue
      </Button>
    </form>
  );
}
